import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const ALLOWED_CATEGORIES = [
  "handyman", "tv-mounting", "furniture-assembly", "moving",
  "furniture-removal", "heavy-lifting", "ikea-assembly", "furniture-disassembly",
  "shelf-mounting", "picture-hanging", "curtain-blind-installation",
  "home-repairs", "appliance-installation", "door-repair", "drywall-repair",
] as const;
type AllowedCategory = (typeof ALLOWED_CATEGORIES)[number];

const InputSchema = z.object({
  prompt: z.string().trim().min(5, "Describe your job in a few words").max(1000),
});

export type AiJobDraft = {
  category_slug: AllowedCategory;
  title: string;
  description: string;
  required_skills: string[];
  estimated_duration_minutes: number;
  urgency: "low" | "normal" | "high";
  confidence: number;
  rationale: string;
};

const SYSTEM_PROMPT = `You are the booking assistant for Peopels.com, a Greater London-only marketplace.
You ONLY handle these service categories (use the slug exactly):
- handyman (small repairs, plumbing fixes, electrical fixes, general odd jobs)
- tv-mounting (mounting TVs on walls, cable concealment, bracket installation)
- furniture-assembly (general flat-pack assembly: wardrobes, beds, desks, shelving)
- moving (man-and-van, lifting, loading, single-item delivery, house moves)
- furniture-removal (sofa/mattress/wardrobe collection and licensed disposal)
- heavy-lifting (extra hands for heavy/awkward lifts without a full van move)
- ikea-assembly (specifically IKEA flat-pack: PAX, MALM, KALLAX, BILLY)
- furniture-disassembly (taking apart beds, wardrobes, flat-pack ahead of moves)
- shelf-mounting (wall shelves on plasterboard, brick or concrete)
- picture-hanging (frames, mirrors, gallery walls)
- curtain-blind-installation (curtain poles, tracks, roller/Roman/Venetian blinds)
- home-repairs (multi-task small repair visits)
- appliance-installation (washing machines, dishwashers, hobs, hoods — non-gas)
- door-repair (re-hanging, planing, hinges, handles, locks on internal doors)
- drywall-repair (patching holes and cracks, plasterboard repairs)

Pick the most specific slug that fits. If the request is generic, fall back to handyman, furniture-assembly or moving. Be concise, specific, and London-aware. Return only the JSON object matching the schema.`;

function extractJson(text: string): unknown {
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const raw = (fenced ? fenced[1] : text).trim();
  // try to locate first { ... } block
  const start = raw.indexOf("{");
  const end = raw.lastIndexOf("}");
  if (start === -1 || end === -1) throw new Error("Model returned no JSON");
  return JSON.parse(raw.slice(start, end + 1));
}

const DraftSchema = z.object({
  category_slug: z.enum(ALLOWED_CATEGORIES),
  title: z.string().trim().min(6).max(120),
  description: z.string().trim().min(20).max(2000),
  required_skills: z.array(z.string().trim().min(1)).min(1).max(8),
  estimated_duration_minutes: z.number().int().min(15).max(8 * 60),
  urgency: z.enum(["low", "normal", "high"]),
  confidence: z.number().min(0).max(1),
  rationale: z.string().trim().max(400),
});

export const generateJobDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => InputSchema.parse(data))
  .handler(async ({ data, context }): Promise<AiJobDraft> => {
    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) throw new Error("AI service unavailable");

    const userPrompt = `Customer request: """${data.prompt}"""

Return JSON with this exact shape:
{
  "category_slug": "handyman" | "tv-mounting" | "furniture-assembly" | "moving",
  "title": "short, specific job title (6-80 chars, no quotes)",
  "description": "2-4 sentence rewrite of the job in clear plain English, restating key details (size, location in home, materials, access, anything tricky). Do NOT invent facts.",
  "required_skills": ["3-6 short skill tags, e.g. 'stud-finding', 'cable concealment'"],
  "estimated_duration_minutes": integer between 15 and 480,
  "urgency": "low" | "normal" | "high",
  "confidence": number between 0 and 1 (how well the request fits an allowed category),
  "rationale": "one short sentence explaining the category + duration choice"
}`;

    const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Lovable-API-Key": apiKey,
        "X-Lovable-AIG-SDK": "raw-fetch",
      },
      body: JSON.stringify({
        model: "google/gemini-3-flash-preview",
        messages: [
          { role: "system", content: SYSTEM_PROMPT },
          { role: "user", content: userPrompt },
        ],
        response_format: { type: "json_object" },
        temperature: 0.2,
      }),
    });

    if (res.status === 429) throw new Error("AI is busy right now — please try again in a moment.");
    if (res.status === 402) throw new Error("AI credits exhausted. Please contact support.");
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new Error(`AI service error (${res.status})${body ? `: ${body.slice(0, 200)}` : ""}`);
    }

    const payload = (await res.json()) as {
      choices?: Array<{ message?: { content?: string } }>;
    };
    const content = payload.choices?.[0]?.message?.content ?? "";
    if (!content) throw new Error("AI returned an empty response");

    const parsed = DraftSchema.parse(extractJson(content));

    // Audit trail — privileged insert (RLS restricts inserts to service role)
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      await supabaseAdmin.from("audit_logs").insert({
        actor_id: context.userId,
        action: "job.ai_draft_generated",
        entity_type: "job_draft",
        after_data: {
          prompt: data.prompt,
          draft: parsed,
          model: "google/gemini-3-flash-preview",
        },
      });
    } catch (err) {
      // Non-fatal: log but don't fail the user request
      console.error("audit_log insert failed", err);
    }

    return parsed;
  });
